import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Save, ArrowLeft, AlertCircle, Loader, Download, Youtube } from 'lucide-react';
import { usePromptStore } from '../../store/promptStore';
import { extractYouTubeVideoId } from '../YouTubeEmbed';
import MDEditor from '@uiw/react-md-editor';
import type { Prompt } from '../../types/prompt';
import DownloadSelectorModal from './DownloadSelectorModal';
import { Calendar } from 'lucide-react';

// Initial form state
const INITIAL_FORM_STATE: Partial<Prompt> = {
  title: '',
  description: '',
  content: '',
  category_id: null,
  youtube_video_id: null,
  download_url: null,
  download_button_text: null,
  is_featured: false,
  is_published: true,
  published_at: null
};

export default function PromptForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { 
    selectedPrompt, 
    categories, 
    tags, 
    loading, 
    error, 
    fetchPromptById, 
    fetchCategories, 
    fetchTags, 
    createPrompt, 
    updatePrompt, 
    clearError,
    clearSelectedPrompt
  } = usePromptStore();

  const [formData, setFormData] = useState<Partial<Prompt>>({...INITIAL_FORM_STATE});
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showDownloadSelector, setShowDownloadSelector] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    fetchCategories();
    fetchTags();
    
    if (id && id !== 'new') {
      fetchPromptById(id);
    } else {
      // Clear selected prompt when creating a new one
      clearSelectedPrompt();
      setFormData({...INITIAL_FORM_STATE});
      setSelectedTags([]);
    }
  }, [id, fetchCategories, fetchTags, fetchPromptById, clearSelectedPrompt]);

  // Update form data when selectedPrompt changes
  useEffect(() => {
    if (selectedPrompt && id !== 'new') {
      setFormData({
        title: selectedPrompt.title,
        description: selectedPrompt.description || '',
        download_url: selectedPrompt.download_url || '',
        youtube_video_id: selectedPrompt.youtube_video_id || '',
        download_button_text: selectedPrompt.download_button_text || '',
        content: selectedPrompt.content,
        category_id: selectedPrompt.category_id || '',
        is_featured: selectedPrompt.is_featured,
        is_published: selectedPrompt.is_published,
        published_at: selectedPrompt.published_at
      });
      
      setSelectedTags(selectedPrompt.tags?.map(tag => tag.id) || []);
    }
  }, [selectedPrompt, id]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.title?.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!formData.content?.trim()) {
      errors.content = 'Content is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Prepare data for submission
    const submissionData = {
      ...formData,
      // Convert empty string category_id to null
      category_id: formData.category_id || null
    };
    
    try {
      setSaving(true);
      clearError();
      
      if (id && id !== 'new') {
        await updatePrompt(id, submissionData, selectedTags);
      } else {
        await createPrompt(submissionData, selectedTags);
      }
      
      // Reset form after successful submission
      setFormData({...INITIAL_FORM_STATE});
      setSelectedTags([]);
      
      navigate('/prompt-admin/prompts');
    } catch (error) {
      console.error('Error saving prompt:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadSelect = (url: string, fileName: string) => {
    setFormData(prev => ({
      ...prev,
      download_url: url,
      download_button_text: prev.download_button_text || `Download ${fileName}`
    }));
    setShowDownloadSelector(false);
  };

  const handleTagToggle = (tagId: string) => {
    setSelectedTags(prev => 
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Link
          to="/prompt-admin/prompts"
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Prompts
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          {id && id !== 'new' ? 'Edit Prompt' : 'New Prompt'}
        </h1>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={clearError}
                className="mt-2 text-sm text-red-700 hover:text-red-900"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6 space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  formErrors.title ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-[#F98B3D] focus:border-[#F98B3D]'
                }`}
              />
              {formErrors.title && (
                <p className="mt-1 text-sm text-red-600">{formErrors.title}</p>
              )}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <input
                type="text"
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#F98B3D] focus:border-[#F98B3D]"
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <select
                id="category"
                value={formData.category_id || ''}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value || null })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#F98B3D] focus:border-[#F98B3D]"
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {tags.map((tag) => (
                  <label
                    key={tag.id}
                    className={`flex items-center p-2 rounded border ${
                      selectedTags.includes(tag.id)
                        ? 'border-[#F98B3D] bg-orange-50'
                        : 'border-gray-300 hover:bg-gray-50'
                    } cursor-pointer transition-colors duration-200`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag.id)}
                      onChange={() => handleTagToggle(tag.id)}
                      className="h-4 w-4 text-[#F98B3D] focus:ring-[#F98B3D] border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">{tag.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Download URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Downloadable Resource
              </label>
              <div className="flex items-start space-x-4">
                <div className="flex-grow">
                  <input
                    type="text"
                    value={formData.download_url || ''}
                    onChange={(e) => setFormData({ ...formData, download_url: e.target.value })}
                    placeholder="Download URL"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#F98B3D] focus:border-[#F98B3D]"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowDownloadSelector(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Browse
                </button>
              </div>
              {formData.download_url && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={formData.download_button_text || ''}
                    onChange={(e) => setFormData({ ...formData, download_button_text: e.target.value })}
                    placeholder="Download Button Text"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#F98B3D] focus:border-[#F98B3D]"
                  />
                </div>
              )}
            </div>

            {/* YouTube Video ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                YouTube Video ID
              </label>
              <div className="flex items-start space-x-4">
                <div className="flex-grow">
                  <input
                    type="text"
                    value={formData.youtube_video_id || ''}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      const videoId = extractYouTubeVideoId(inputValue);
                      setFormData({ 
                        ...formData, 
                        youtube_video_id: videoId || inputValue || null 
                      });
                    }}
                    placeholder="e.g., dQw4w9WgXcQ"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#F98B3D] focus:border-[#F98B3D]"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the YouTube video URL or ID (we'll extract the ID automatically)
                  </p>
                </div>
                <div className="flex-shrink-0 flex items-center">
                  <Youtube className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              {formData.youtube_video_id && (
                <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                  <div className="aspect-video">
                    <iframe 
                      width="100%" 
                      height="100%" 
                      src={`https://www.youtube.com/embed/${formData.youtube_video_id}`}
                      title="YouTube video player" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                Content *
              </label>
              <div data-color-mode="light" className="markdown-editor-wrapper">
                <MDEditor
                  value={formData.content || ''}
                  onChange={(value) => setFormData({ ...formData, content: value || '' })}
                  height={500}
                  preview="edit"
                  highlightEnable={true}
                  enableScroll={true}
                  commands={[
                    'bold', 'italic', 'strikethrough', 'title', 'divider',
                    'unordered-list', 'ordered-list', 'divider',
                    'link', 'quote', 'code', 'divider',
                    'fullscreen'
                  ]}
                  extraCommands={[
                    {
                      name: 'help',
                      keyCommand: 'help',
                      buttonProps: { 'aria-label': 'Help' },
                      icon: (
                        <svg viewBox="0 0 16 16" width="12px" height="12px">
                          <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8Zm0 14c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6Z" />
                          <path d="M8 4C6.9 4 6 4.9 6 6h2c0-.6.4-1 1-1s1 .4 1 1c0 .3-.1.5-.3.7l-1.8 1.8C7.3 8.9 7 9.5 7 10v1h2v-1c0-.3.1-.5.3-.7l1.8-1.8c.6-.6 1-1.5 1-2.5 0-2.2-1.8-4-4-4Z" />
                          <path d="M8 13a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
                        </svg>
                      ),
                      execute: () => {
                        window.open('https://www.markdownguide.org/basic-syntax/', '_blank');
                      },
                    }
                  ]}
                />
              </div>
              {formErrors.content && (
                <p className="mt-1 text-sm text-red-600">{formErrors.content}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Use Markdown formatting for better readability.
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Include an <code>## Overview</code> section after the title and before the <code>## Task</code> section to provide a brief description of what the prompt does.
              </p>
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={formData.is_published !== false}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="h-4 w-4 text-[#F98B3D] focus:ring-[#F98B3D] border-gray-300 rounded"
                />
                <label htmlFor="is_published" className="ml-2 block text-sm text-gray-900">
                  Published
                </label>
              </div>
              
              <div className="mt-4 ml-6">
                <label htmlFor="published_date" className="block text-sm font-medium text-gray-700">
                  Published Date
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    id="published_date"
                    value={formData.published_at || ''}
                    onChange={(e) => setFormData({ ...formData, published_at: e.target.value || null })}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#F98B3D] focus:border-[#F98B3D]"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 max-w-xs">
                  Set the date when this prompt was or will be published
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 text-right">
            <button
              type="button"
              onClick={() => navigate('/prompt-admin/prompts')}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 mr-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader className="animate-spin h-5 w-5 mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Save Prompt
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Download Selector Modal */}
      <DownloadSelectorModal
        isOpen={showDownloadSelector}
        onClose={() => setShowDownloadSelector(false)}
        onSelect={handleDownloadSelect}
      />
    </div>
  );
}